import React from "react";
import styled from "styled-components/macro";
import { FormattedMessage, injectIntl, FormattedNumber } from "react-intl";
import { SUGGESTIONS } from "../SearchMap/SearchMap";
import Address from "../SearchMap/Address";
import RangeSlider from "../../components/RangeSlider";
import Checkbox from "../../components/Checkbox";
import FloatingInput from "../../components/FloatingInput";
import Button from "../../components/Button";
import Icon from "../../components/Icon";
import Loading from "../../components/Loading";
import { CURRENCY_FORMAT, truncate } from "../../utils/formatters";
import { hasPosition } from "../../utils/calc";
import { DEFAULT_FILTERS } from "../../store/reducers/search";

const MAX_PRICE = 500;
const RADIUS_OPTIONS = [5, 10, 20, 30, 50, 100, 150];

// The search assistant can set a radius outside the presets, which a select with an unmatched
// value would render as the first option instead of the radius actually in use
const radiusOptions = radius =>
  RADIUS_OPTIONS.includes(radius)
    ? RADIUS_OPTIONS
    : [...RADIUS_OPTIONS, radius].sort((first, second) => first - second);

const SearchForm = styled(FloatingInput)`
  position: static !important;
  padding: 0.15rem !important;
`;

const Actions = styled.div`
  display: flex;
  margin-bottom: 0.25rem;
  margin-left: 0.25rem;
  position: relative;
  flex-shrink: 0;
  justify-content: center;
  flex-direction: column;

  ${({ sticky, theme }) =>
    sticky &&
    `
      position: sticky;
      top: 3rem;
      margin: 0 auto;
      border-radius: 2rem;
      background-color: rgba(255, 255, 255, 0.8);
      backdrop-filter: blur(8px);
      box-shadow: ${theme.boxShadowLifted};
      padding: 0.5rem;
      z-index: 11;
      width: calc(100% - 2rem);
      max-width: 480px;
    `};

  @media (${props => props.theme.tabletScreen}) {
    max-width: 800px;
  }
`;

const ActionsRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  min-width: 100%;
`;

const ActionDisplay = styled.aside`
  display: flex;
`;

const ResultsDisplay = styled.div`
  min-height: 50px;
  border-radius: 2rem;
  margin-left: 0.5rem;
  display: flex;
  align-items: center;
  justify-content: center;
  text-align: center;
  max-width: 185px;
  padding: 0 0.5rem;
  line-height: 1rem;
  color: ${props => props.theme.black};
  cursor: pointer;

  & > div {
    padding: 0;
    margin-left: 0.5rem;
    & > div {
      margin: 0;
    }
  }
`;

const Filters = styled.div`
  flex: 1;
  margin-top: 1rem;
  padding-left: 1rem;
  padding-right: 1rem;
  padding-bottom: 1rem;
  max-height: 280px;
  overflow-x: hidden;
  overflow-y: auto;

  display: flex;
  flex-wrap: wrap;

  @media (${props => props.theme.tabletScreen}) {
    max-height: 520px;
  }
`;

const Filter = styled.section`
  margin: 1rem 0;
  min-width: 300px;
  flex: 1;
  padding: 0;

  @media (${props => props.theme.tabletScreen}) {
    padding: 0 1rem;
  }
`;

const RadiusSelect = styled.select`
  margin: 0.5rem 0;
  padding: 0.5rem 1rem;
  width: 100%;
  border-radius: 2rem;
  border: 1px solid ${props => props.theme.light};
  box-shadow: ${props => props.theme.boxShadowInset};
  background-color: ${props => props.theme.light};
  color: ${props => props.theme.dark};
  font-family: inherit;
  font-size: 100%;
  cursor: pointer;

  &:hover,
  &:focus {
    color: ${props => props.theme.black};
    border-color: ${props => props.theme.primary};
  }
`;

const Checkboxes = styled.div`
  display: flex;
  margin: 0.5rem 0;
  flex-direction: column;
  align-items: flex-start;
`;

const AddressDisplay = styled.span`
  position: absolute;
  bottom: -1rem;
  right: 0;
  background-color: ${props => props.theme.info};
  box-shadow: ${props => props.theme.boxShadow};
  color: ${props => props.theme.white};
  font-family: ${props => props.theme.fontFamilyMonospace};
  padding: 0.125rem 0.5rem;
  border-radius: 5rem;
  white-space: nowrap;
  max-width: 200px;
  overflow: hidden;
  text-overflow: ellipsis;

  @media (${props => props.theme.tabletScreen}) {
    max-width: 300px;
  }
`;

const SearchBar = ({
  isLoading,
  stateTerm,
  editFilters,
  setEditFilters,
  totalCount,
  filters,
  setFilters,
  center,
  address,
  onAddressSelect,
  onAddressClear,
  onMaxPriceChange,
  onRadiusChange,
  updateFilters,
  onSubmit,
  showLocation = true,
  intl,
}) => (
  <Actions sticky>
    <ActionsRow>
      <SearchForm
        name="search"
        defaultValue={stateTerm}
        busy={isLoading}
        onSubmit={onSubmit}
        defaultExpanded={true}
        placeholder={intl.formatMessage({ id: "pages.SearchMap.all" })}
        suggestions={SUGGESTIONS}
      />
      <ActionDisplay>
        <ResultsDisplay>
          {isLoading ? (
            <Loading />
          ) : (
            <FormattedMessage id="pages.SearchMap.results" values={{ count: totalCount }} />
          )}
        </ResultsDisplay>
        <Button
          color={editFilters ? "info" : "default"}
          onClick={() => setEditFilters(!editFilters)}
          style={{ opacity: 1 }}
          round
        >
          <Icon name="Filter" />
        </Button>
      </ActionDisplay>
    </ActionsRow>
    {editFilters ? (
      <ActionsRow>
        <Filters>
          <Filter>
            <FormattedMessage id="pages.Search.filters.location" />
            <Address
              onClear={onAddressClear}
              onSelect={(address, { lat, lng }) => onAddressSelect(address, lat, lng)}
              address={address}
              defaultExpanded
            />
          </Filter>
          {hasPosition(center) && (
            <Filter>
              <FormattedMessage id="pages.Search.filters.radius" />
              <RadiusSelect
                value={filters.radius || DEFAULT_FILTERS.radius}
                onChange={event => onRadiusChange(parseInt(event.target.value, 10))}
              >
                {radiusOptions(filters.radius || DEFAULT_FILTERS.radius).map(radius => (
                  <option key={radius} value={radius}>
                    {intl.formatMessage({ id: "pages.Search.filters.radiusOption" }, { radius })}
                  </option>
                ))}
              </RadiusSelect>
            </Filter>
          )}
          <Filter>
            <FormattedMessage id="pages.Search.filters.age" />
            <Checkboxes>
              <Checkbox
                active={(filters.ageGroups || []).includes("baby")}
                onClick={() => updateFilters("ageGroups", ["baby"])}
              >
                <Icon name="baby" size="small" />
                <FormattedMessage id="pages.Search.filters.ageBaby" />
              </Checkbox>
              <Checkbox
                active={(filters.ageGroups || []).includes("child")}
                onClick={() => updateFilters("ageGroups", ["child"])}
              >
                <Icon name="children" size="small" />
                <FormattedMessage id="pages.Search.filters.ageChild" />
              </Checkbox>
              <Checkbox
                active={(filters.ageGroups || []).includes("parents")}
                onClick={() => updateFilters("ageGroups", ["parents"])}
              >
                <Icon name="person-dress" size="small" />
                <FormattedMessage id="pages.Search.filters.ageParents" />
              </Checkbox>
            </Checkboxes>
          </Filter>
          <Filter>
            <FormattedMessage id="pages.Search.filters.type" />
            <Checkboxes>
              <Checkbox
                active={(filters.locationTypes || []).includes("online")}
                onClick={() => updateFilters("locationTypes", ["online"])}
              >
                <Icon name="display" size="small" />
                <FormattedMessage id="course.attributes.locationType.online" />
              </Checkbox>
              <Checkbox
                active={center && (filters.locationTypes || []).includes("on_site")}
                disabled={!center}
                onClick={() => updateFilters("locationTypes", ["on_site"])}
              >
                <Icon name="location-dot" size="small" />
                <FormattedMessage id="course.attributes.locationType.onSite" />
              </Checkbox>
              <Checkbox
                active={(filters.locationTypes || []).includes("home", "by_arrangement")}
                onClick={() => updateFilters("locationTypes", ["home", "by_arrangement"])}
              >
                <Icon name="Home" size="small" />
                <FormattedMessage id="course.attributes.locationType.others" />
              </Checkbox>
            </Checkboxes>
          </Filter>
          <Filter>
            <FormattedMessage id="pages.Search.filters.price" />
            <RangeSlider
              onChange={onMaxPriceChange}
              from={0}
              to={MAX_PRICE}
              format={v => (
                <span>
                  <span>{v >= MAX_PRICE ? "> " : "max "}</span>
                  <FormattedNumber value={v} {...CURRENCY_FORMAT} maximumFractionDigits={0} />
                </span>
              )}
              defaultValue={((filters || {}).maxPrice && parseInt(filters.maxPrice)) || MAX_PRICE}
            />
          </Filter>
          <Filter></Filter>
          <ActionsRow style={{ justifyContent: "space-between" }}>
            <Button size="small" onClick={() => setFilters(DEFAULT_FILTERS)} color="dark" outline>
              <FormattedMessage id="actions.reset" />
            </Button>
            <Button onClick={() => setEditFilters(false)} color="info">
              <FormattedMessage id="actions.apply" />
            </Button>
          </ActionsRow>
        </Filters>
      </ActionsRow>
    ) : (
      showLocation && (
        <AddressDisplay onClick={() => setEditFilters(true)}>
          {hasPosition(center) ? (
            <small>
              {truncate(address || <FormattedMessage id="pages.Search.yourPosition" />, 20)}
              <small>{`, ${filters.radius} km`}</small>
            </small>
          ) : (
            <FormattedMessage id="pages.Search.noPosition" />
          )}
        </AddressDisplay>
      )
    )}
  </Actions>
);

export default injectIntl(SearchBar);
